package org.vreo.devicehub.features.liveAccess.data.repository

import androidx.compose.ui.graphics.ImageBitmap
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.conflate
import kotlinx.coroutines.flow.flowOn
import kotlinx.coroutines.flow.sample
import kotlin.time.Duration.Companion.milliseconds

import org.vreo.devicehub.features.core.data.appLogger.AppLogger
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.EmptyResult
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.domain.device.device.networking.RemoteDeviceControl
import org.vreo.devicehub.features.liveAccess.domain.LiveAccessRepository
import org.vreo.devicehub.features.liveAccess.domain.WebRtcConnectMode
import org.vreo.devicehub.features.liveAccess.domain.WebRtcController

/**
 * Live access repository implementation (default) for connecting and accessing device live stream
 *
 * @property webRtc WebRTC controller for handling platform specific WebRtc operations
 */
class LiveAccessRepositoryDefault(
    val iotMessaging: RemoteDeviceControl,
    val webRtc: WebRtcController
) : LiveAccessRepository {
    override suspend fun connect(
        token: String,
        deviceId: String,
        mode: WebRtcConnectMode
    ): EmptyResult<DataError.Remote> {
        // Set mode
        webRtc.setMode(mode)

        // Create and send WebRTC offer to device
        val offer = webRtc.createOffer()

        // Get response WebRTC (answer) configuration from device
        val config = when (val requestResult = iotMessaging.getWebRtcOffer(token, deviceId, offer)) {
            is Result.Success -> {
                requestResult.data
            }

            is Result.Error -> {
                return Result.Error(requestResult.error)
            }
        }

        // Process WebRTC answer
        webRtc.setRemoteAnswer(config)
        return Result.Success(Unit)
    }

    override suspend fun disconnect(): EmptyResult<DataError.Remote> {
        webRtc.disconnect()
        return Result.Success(Unit)
    }

    override suspend fun enableVideo(enable: Boolean): EmptyResult<DataError.Remote> {
        return try {
            webRtc.enableVideo(enable)
            Result.Success(Unit)
        } catch (e: Exception) {
            AppLogger.error("Failed to enable video, error: ${e.message}")
            Result.Error(DataError.Remote.SERVICE_UNAVAILABLE)
        }
    }

    @OptIn(FlowPreview::class)
    override fun getVideoStream(): Flow<ImageBitmap> {
        // Get video stream from WebRTC controller and convert it to ImageBitmap.
        return webRtc.getVideoStream()
            .conflate()
            .sample(167.milliseconds)
            .flowOn(Dispatchers.Default)
    }
}